


















































import {
  Params,
  SortedStreamGraphComponent,
  SortedStreamGraphConfig,
  SortedStreamGraphData,
  SortedStreamGraphDataItem,
  SortedStreamGraphDataItemValue
} from '@empathy/insights';
import { deepEqual } from '@empathybroker/deep-equal';
import { KeywordsTrendsModeEnum } from '@empathybroker/tsclient-stats/dist/enums/keywords-trends-mode.enum';
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum';
import { DailyKeywordData } from '@empathybroker/tsclient-stats/dist/models/daily-keywords/daily-keyword-data';
import { KeywordsTrendsDataItem } from '@empathybroker/tsclient-stats/dist/models/keywords-trends/keywords-trends-data-item';
import { KeywordData as KeywordsDataItem } from '@empathybroker/tsclient-stats/dist/models/keywords/keyword-data';
import { TermSuccessDataPoint } from '@empathybroker/tsclient-stats/dist/models/termsuccessreport/termsuccessdata-point';

import { mapActions } from 'vuex';

import { Badge, KeywordsAside, KeywordSuggestion, Searchbox, Toggle, UiAlert } from '../../components';

import { FilterIdEnum, KeywordsModeEnum, KpiEnum } from '../../enums';

import { InsightsMixin } from '../../mixins';

import { Dictionary, KeywordsMessages } from '../../models';

import { CONTEXT_CONSTS } from '../../store/context/context.consts';

interface KeywordsData {
  top: Dictionary<KeywordData[]>;
  search: QueryData[];
  selected: KeywordData[];
}

interface KeywordData {
  overall: KeywordsDataItem | KeywordsTrendsDataItem;
  dist: DailyKeywordData[];
  queries: TermSuccessDataPoint[] | undefined;
}

interface QueryData {
  query: string;
  results: KeywordData[];
}

enum DataGroupingType {
  DAILY,
  WEEKLY,
  MONTHLY
}

export default {
  name: 'keywords',
  mixins: [
    InsightsMixin
  ],
  props: {
    loading: {
      type: Object, // { visible: boolean; }
      required: true
    }
  },
  components: {
    Searchbox,
    KeywordSuggestion,
    Toggle,
    Badge,
    UiAlert,
    KeywordsAside
  },
  computed: {
    searchResults: function (): KeywordData[] {
      if (!this.data || !this.context.query) {
        return [];
      }
      const searchData = this.data.search.find((queryData: QueryData) => (queryData.query === this.context.query));
      return (!!searchData ? searchData.results : []);
    },
    searchSuggestions: function (): { value: string; }[] {
      return this.searchResults.map((keywordData: KeywordData) => ({
        value: keywordData.overall.keyword
      }));
    },
    isCompareMode: function (): boolean {
      return (
        !!this.data &&
        !!this.data.selected.length &&
        this.data.selected.some((selectedKeywordData: KeywordData) =>
          !this.data.top[this.mode]?.some((topKeywordData: KeywordData) =>
            (topKeywordData.overall.keyword === selectedKeywordData.overall.keyword)))
      );
    },
    title: function (): string {
      if (this.isCompareMode) {
        return this.messages.main.titles.compare;
      }
      return this.$epb.utils.compileMessage(this.messages.main.titles.top, {
        count: this.maxKeywordsTop,
        mode: this.messages.main.modes[this.mode]
      });
    },
    modeOptions: function (): { id: KeywordsModeEnum; label: string; }[] {
      return Object.values(KeywordsModeEnum).map((id: KeywordsModeEnum) => ({
        id,
        label: (this.messages.main.modes[id] || id).toUpperCase()
      }));
    },
    componentParams: function (): Params<SortedStreamGraphConfig, SortedStreamGraphData> {
      return {
        config: {
          locale: this.$epb.locale,
          itemNameWidth: 120,
          valueNameHeight: 40,
          valueNameWidth: 65,
          valueSuffix: this.$epb.messages.analytics.kpis[KpiEnum.QUERIES].valueLabel.toUpperCase(),
          areaOpacity: 0.5,
          deselectIconCls: 'eicon',
          deselectIconContent: 'close'
        },
        data: this.getComponentData()
      };
    },
    selectedKeywords: function (): KeywordData[] {
      return this.data?.selected || [];
    },
    messages: function (): KeywordsMessages {
      return this.$epb.messages.insights.keywords;
    }
  },
  data: function () {
    return {
      maxKeywordsTop: 10,
      maxQueriesPerKeyword: 5,
      maxSearchesSaved: 10,
      mode: KeywordsModeEnum.GROWTH,
      component: undefined
    };
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery
    }),
    fetchData: async function (cleanData: boolean, keepSelected = true): Promise<KeywordsData> {
      const { top, search, selected } = (cleanData || !this.data) ? {
        top: {},
        search: [],
        selected: []
      } : this.data;
      if (!top[this.mode]) {
        top[this.mode] = await this.fetchTopData(this.mode, top);
      }
      if (!!this.context.query) {
        const queryAlreadySearched = search.some((queryData: QueryData) => queryData.query === this.context.query);
        if (!queryAlreadySearched) {
          const queryKeywordsData = await this.fetchSearchData(this.context.query, top);
          search.unshift({
            query: this.context.query,
            results: queryKeywordsData
          });
          search.splice(this.maxSearchesSaved);
        }
      }
      return {
        top,
        search,
        selected: (keepSelected ? selected : [])
      };
    },
    fetchTopData: async function (mode: KeywordsModeEnum, top: Dictionary<KeywordData[]> = {}): Promise<KeywordData[]> {
      const reportResponse = await this.fetchStatsReport({
        id: ((mode === KeywordsModeEnum.GROWTH) ? StatsReportsIdsEnum.KEYWORDS_TRENDS : StatsReportsIdsEnum.KEYWORDS),
        params: {
          rows: this.maxKeywordsTop,
          ...((mode === KeywordsModeEnum.GROWTH) && { mode: KeywordsTrendsModeEnum.SIGNIFICANCE })
        }
      });
      return await Promise.all(
        reportResponse.data.map(async (overall: KeywordsDataItem | KeywordsTrendsDataItem) => this.fetchKeywordData(overall, top))
      );
    },
    fetchKeywordData: async function (overall: KeywordsDataItem | KeywordsTrendsDataItem,
      top: Dictionary<KeywordData[]>): Promise<KeywordData> {
      const topKeywordData = this.findKeywordDataInTop(overall.keyword, top);
      const { dist, queries } = (!!topKeywordData ? topKeywordData : {
        dist: await this.fetchKeywordDistData(overall.keyword),
        queries: undefined
      });
      return {
        overall,
        dist,
        queries
      };
    },
    findKeywordDataInTop: function (keyword: string, top: Dictionary<KeywordData[]>): KeywordData {
      for (const topData of Object.values(top)) {
        const keywordData = topData.find((topKeywordData: KeywordData) => (topKeywordData.overall.keyword === keyword));
        if (!!keywordData) {
          return keywordData;
        }
      }
    },
    fetchKeywordDistData: async function (keyword: string): Promise<DailyKeywordData[]> {
      const reportResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.DAILY_KEYWORDS,
        params: {
          keyword
        }
      });
      return reportResponse.data.dist;
    },
    fetchSearchData: async function (query: string, top: Dictionary<KeywordData[]> = {}): Promise<KeywordData[]> {
      const reportResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.KEYWORDS,
        params: {
          query
        }
      });
      return await Promise.all(
        reportResponse.data.map(async (overall: KeywordsDataItem) => this.fetchKeywordData(overall, top))
      );
    },
    getSearchSuggestionData: function (value: string): KeywordData {
      return this.searchResults.find((keywordData: KeywordData) => (keywordData.overall.keyword === value));
    },
    getComponentData: function (): SortedStreamGraphData {
      if (!this.data) {
        return [];
      }
      const keywordsData = (this.isCompareMode ? this.data.selected : (this.data.top[this.mode] || []));
      return keywordsData.map((keywordData: KeywordData) => ({
        name: keywordData.overall.keyword.toUpperCase(),
        values: this.transformComponentDataItemValues(keywordData),
        selected: this.data.selected.some((selectedKeywordData: KeywordData) =>
          (selectedKeywordData.overall.keyword === keywordData.overall.keyword))
      }));
    },
    transformComponentDataItemValues: function (keywordData: KeywordData): SortedStreamGraphDataItemValue[] {
      const startDate = keywordData.dist[0].date;
      const endDate = keywordData.dist[keywordData.dist.length - 1].date;
      const groupingType = this.getComponentDataGroupingType(startDate, endDate);
      let dataItemValue: SortedStreamGraphDataItemValue;
      return keywordData.dist.reduce((dataItemValues: SortedStreamGraphDataItemValue[],
        keywordDistDataItem: DailyKeywordData) => {
        const { name, tooltipName } = this.getComponentDataItemValueNames(groupingType, keywordDistDataItem.date, startDate, endDate);
        if (!dataItemValue || (name !== dataItemValue.name)) {
          dataItemValue = {
            name,
            tooltipName,
            value: 0
          };
          dataItemValues.push(dataItemValue);
        }
        dataItemValue.value += keywordDistDataItem.queryCount;
        return dataItemValues;
      }, []);
    },
    getComponentDataGroupingType: function (startDate: Date, endDate: Date): DataGroupingType {
      const periodDurationInDays = ((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      if (periodDurationInDays < 14) {
        return DataGroupingType.DAILY;
      }
      if (periodDurationInDays < 57) {
        return DataGroupingType.WEEKLY;
      }
      return DataGroupingType.MONTHLY;
    },
    getComponentDataItemValueNames: function (groupingType: DataGroupingType, valueDate: Date, startDate: Date,
      endDate: Date): { name: string, tooltipName: string } {
      return {
        [DataGroupingType.DAILY]: this.getComponentDailyDataItemValueNames,
        [DataGroupingType.WEEKLY]: this.getComponentWeeklyDataItemValueNames,
        [DataGroupingType.MONTHLY]: this.getComponentMonthlyDataItemValueNames
      }[groupingType](valueDate, startDate, endDate);
    },
    getComponentDailyDataItemValueNames: function (valueDate: Date, startDate: Date,
      endDate: Date): { name: string, tooltipName: string } {
      const dayString = valueDate.toLocaleDateString(this.$epb.locale, {
        day: '2-digit'
      });
      const weekDayString = valueDate.toLocaleDateString(this.$epb.locale, {
        weekday: 'short'
      });
      const name = `${ dayString }<br>${ weekDayString }`.toUpperCase();
      const tooltipName = valueDate.toLocaleDateString(this.$epb.locale, {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      }).toUpperCase();
      return { name, tooltipName };
    },
    getComponentWeeklyDataItemValueNames: function (valueDate: Date, startDate: Date,
      endDate: Date): { name: string, tooltipName: string } {
      const weekDay = ((valueDate.getDay() === 0) ? 6 : (valueDate.getDay() - 1));
      const firstDayOfWeek = new Date(valueDate.getFullYear(), valueDate.getMonth(), (valueDate.getDate() - weekDay));
      const lastDayOfWeek = new Date(firstDayOfWeek.getFullYear(), firstDayOfWeek.getMonth(), (firstDayOfWeek.getDate() + 6));
      const firstDay = ((startDate.getTime() > firstDayOfWeek.getTime()) ? startDate : firstDayOfWeek);
      const lastDay = ((endDate.getTime() < lastDayOfWeek.getTime()) ? endDate : lastDayOfWeek);
      const isSameMonth = (firstDay.getMonth() === lastDay.getMonth());
      const firstDayDayString = firstDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit'
      });
      const firstDayMonthString = firstDay.toLocaleDateString(this.$epb.locale, {
        month: 'short'
      });
      const firstDayString = firstDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
        month: 'short'
      });
      const lastDayString = lastDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
        month: (!isSameMonth ? 'short' : undefined)
      });
      const name = (isSameMonth ? `${ firstDayMonthString }<br>${ firstDayDayString }-${ lastDayString }`
        : `${ firstDayString }-<br>${ lastDayString }`).toUpperCase();
      const isSameYear = (firstDay.getFullYear() === lastDay.getFullYear());
      const firstDayMonthTooltip = firstDay.toLocaleDateString(this.$epb.locale, {
        month: 'short'
      });
      const firstDayStringTooltip = firstDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
        year: (!isSameYear ? 'numeric' : undefined)
      });
      const lastDayStringTooltip = lastDay.toLocaleDateString(this.$epb.locale, {
        month: (!isSameMonth ? 'short' : undefined),
        day: '2-digit'
      });
      const lastDayYearTooltip = lastDay.toLocaleDateString(this.$epb.locale, {
        year: 'numeric'
      });
      const tooltipName = `${ firstDayMonthTooltip } ${ firstDayStringTooltip }-${ lastDayStringTooltip },
      ${ lastDayYearTooltip }`.toUpperCase();
      return { name, tooltipName };
    },
    getComponentMonthlyDataItemValueNames: function (valueDate: Date, startDate: Date,
      endDate: Date): { name: string, tooltipName: string } {
      const valueDateString = valueDate.toLocaleDateString(this.$epb.locale, {
        month: 'short'
      });
      const valueDateTooltipString = valueDate.toLocaleDateString(this.$epb.locale, {
        month: 'short',
        year: 'numeric'
      });
      let name = valueDateString.toUpperCase();
      let tooltipName = valueDateTooltipString.toUpperCase();
      const firstDayOfMonth = new Date(valueDate.getFullYear(), valueDate.getMonth(), 1);
      const lastDayOfMonth = new Date(valueDate.getFullYear(), (valueDate.getMonth() + 1), 0);
      const dateValueMessages = this.$epb.messages.filters.values[FilterIdEnum.DATE];
      if (startDate.getTime() > firstDayOfMonth.getTime()) {
        const startDateString = startDate.toLocaleDateString(this.$epb.locale, {
          day: '2-digit'
        });
        const from = this.$epb.utils.compileMessage(dateValueMessages.from, {
          date: startDateString
        });
        name += `<br>(${ from })`;
        tooltipName += ` (${ from })`;
      } else if (endDate.getTime() < lastDayOfMonth.getTime()) {
        const endDateString = endDate.toLocaleDateString(this.$epb.locale, {
          day: '2-digit'
        });
        const to = this.$epb.utils.compileMessage(dateValueMessages.to, {
          date: endDateString
        });
        name += `<br>(${ to })`;
        tooltipName += ` (${ to })`;
      }
      return { name, tooltipName };
    },
    onContextChange: async function (): Promise<void> {
      const filtersHaveChanged = (!this.previousContext || !deepEqual(this.context.filters, this.previousContext.filters));
      await this.loadData(filtersHaveChanged);
    },
    onQueryChange: async function (query: string): Promise<void> {
      await this.setQuery(query || undefined);
      this.$emit('contextChanged');
    },
    onSearchSuggestionSelect: async function (value: string): Promise<void> {
      const keywordAlreadySelected = this.data.selected.some((keywordData: KeywordData) => (keywordData.overall.keyword === value));
      if (!keywordAlreadySelected) {
        const keywordDataToSelect = this.searchResults.find((keywordData: KeywordData) => (keywordData.overall.keyword === value));
        this.data.selected.unshift(keywordDataToSelect);
        if (!keywordDataToSelect.queries) {
          keywordDataToSelect.queries = await this.fetchQueriesData(value);
        }
      }
    },
    fetchQueriesData: async function (keyword: string): Promise<TermSuccessDataPoint[]> {
      const termSuccessReportResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.TERM_SUCCESS,
        params: {
          terms: keyword,
          rows: this.maxQueriesPerKeyword
        }
      });
      return termSuccessReportResponse.data;
    },
    onDataItemClick: async function (dataItem: SortedStreamGraphDataItem): Promise<void> {
      const topKeywordData = (this.data.top[this.mode] || []).find((keywordData: KeywordData) =>
        (keywordData.overall.keyword.toLowerCase() === dataItem.name.toLowerCase()));
      if (!!topKeywordData && !this.data.selected.includes(topKeywordData)) {
        this.data.selected.unshift(topKeywordData);
        if (!topKeywordData.queries) {
          topKeywordData.queries = await this.fetchQueriesData(topKeywordData.overall.keyword);
        }
      }
    },
    onDataItemDeselectClick: function (dataItem: SortedStreamGraphDataItem): void {
      this.data.selected = this.data.selected.filter((keywordData: KeywordData) =>
        keywordData.overall.keyword.toLowerCase() !== dataItem.name.toLowerCase()
      );
    }
  },
  created: function (): void {
    this.component = new SortedStreamGraphComponent();
    this.component.onDataItemClick = (dataItem: SortedStreamGraphDataItem) => this.onDataItemClick(dataItem);
    this.component.onDataItemDeselectClick = (dataItem: SortedStreamGraphDataItem) => this.onDataItemDeselectClick(dataItem);
  },
  mounted: function (): void {
    this.component.init(this.$refs.component, this.componentParams);
  },
  beforeDestroy: function (): void {
    this.component.destroy();
  },
  watch: {
    mode: {
      handler: async function (): Promise<void> {
        await this.loadData(false, false);
      }
    },
    componentParams: {
      handler: function (params: Params<SortedStreamGraphConfig, SortedStreamGraphData>): void {
        this.component.update(params);
      },
      deep: true
    }
  }
};

